import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PageForm from '../../templates/PageForm';
import TextEdit from '../../molecules/TextEdit';
import useFetch from '../../../hooks/useFetch';
import { useSignal } from '@preact/signals-react';
import { IoArrowForwardOutline } from 'react-icons/io5';
import { IoMdArrowRoundBack } from 'react-icons/io';
import { FaQuestionCircle } from 'react-icons/fa';
import NewTooltip from '../../atoms/NewTooltip';
import SpanList, { ErrorItemType } from '../../atoms/SpanList';
import Dialog from '../../organisms/Dialog';
import appConfig from '../../../config/app-config';

const DEFAULT_ACCOUNT_ID = '111257797516';
const buildSesTemplateLink = (name: string, region: string, account: string) =>
    `https://console.aws.amazon.com/ses/home?region=${region}#/templates/${name}?accountId=${account}`;
const buildSesConfigSetLink = (name: string, region: string, account: string) =>
    `https://console.aws.amazon.com/ses/home?region=${region}#/configuration-sets/${name}?accountId=${account}`;

interface TemplateWppData {
    template_name: string;
    configuration_set?: string;
    params: Param[];
}

export interface TemplateWpp {
    pk: string;
    sk: string;
    description: string;
    templates: TemplateWppData[];
    variables: any[];
    params: Param[];
    users: string[];
    identifier?: string[];
    groupedItems?: string[];
}

type Param = {
    block: 'HEADER' | 'CONTENT' | 'BODY';
    type: string;
    name: string;
    field?: string;
};

const COMMUNICATION_ENV = appConfig.apis.communicationApiEnv;
const API_KEY = COMMUNICATION_ENV.key;
const NOTIFICATION_URL = COMMUNICATION_ENV.address;

const TemplateViewPage = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const template = useSignal<TemplateWpp>({
        pk: '',
        sk: '',
        description: '',
        variables: [],
        params: [],
        users: [],
        templates: [
            {
                template_name: '',
                params: [],
            },
        ],
    });

    const apiVersion = useSignal<string>('');
    const hoveredVariable = useSignal<string | null>(null);
    const showInfo = useSignal<boolean>(false);
    const activeTab = useSignal<string>('template');
    const sesTemplates = useSignal<any[]>([]);

    const { isLoading, data, error, isTokenError } = useFetch(`${NOTIFICATION_URL}/templates/${id}/page-view`, [
        { key: 'x-api-key', value: API_KEY },
    ]);

    const region = data?.region || 'us-east-1';
    const accountId = data?.accountId || DEFAULT_ACCOUNT_ID;

    if (isTokenError) navigate('/denied');
    if (error) navigate('/network-error');

    useEffect(() => {
        if (data) {
            apiVersion.value = `${data.apiName} - ${data.apiVersion}`;

            // Carregar templates SES se for MAIL_TEMPLATE
            if (data.pk === 'MAIL_TEMPLATE' && data.sesTemplates) {
                sesTemplates.value = data.sesTemplates;
            }

            const parseParams = () => {
                if (data.pk === 'WPP_TEMPLATE') {
                    return data.templates.map((template: any) => ({
                        template_name: template.template_name,
                        params: [
                            ...template.headers.map((header: any) => ({
                                block: 'HEADER',
                                type: header.type,
                                name: header.document.filename,
                            })),
                            ...template.content.map((content: any, index: number) => ({
                                block: 'CONTENT',
                                type: content.type,
                                name: `{{${index + 1}}}`,
                                field: content.text,
                            })),
                        ],
                    }));
                }

                if (data.pk === 'MAIL_TEMPLATE') {
                    return data.templates.map((template: any) => ({
                        template_name: template.template_name,
                        configuration_set: template.configuration_set,
                        params: [
                            { block: 'HEADER', type: 'Assunto do email (Subject)', name: template.subject },
                            {
                                block: 'HEADER',
                                type: 'Resposta do email (Reply-To)',
                                name: Array.isArray(template.replyTo) ? template.replyTo.join(', ') : template.replyTo,
                            },
                            { block: 'HEADER', type: 'Origem do email (From)', name: template.from },
                            { block: 'HEADER', type: 'Anexo do email (Attachment)', name: template.attachments[0] },
                            ...(template.cc
                                ? [{ block: 'HEADER', type: 'Copia do email (CC)', name: template.cc }]
                                : []),
                            ...(template.bcc
                                ? [
                                    {
                                        block: 'HEADER',
                                        type: 'Copia oculta do email (BCC)',
                                        name: Array.isArray(template.bcc) ? template.bcc.join(', ') : template.bcc,
                                    },
                                ]
                                : []),
                            ...template.body.map((content: any, index: number) => ({
                                block: 'CONTENT',
                                type: 'text',
                                name: `{{${index + 1}}}`,
                                field: content,
                            })),
                        ],
                    }));
                }
                return [];
            };

            const generalParams: Param[] = [
                {
                    block: 'BODY',
                    type: data.pk === 'MAIL_TEMPLATE' ? 'Email do destinatário (To)' : 'Telefone do destinatário (To)',
                    name: data.to,
                },
            ];

            template.value = {
                pk: data.pk,
                sk: data.sk,
                description: data.description,
                templates: parseParams(),
                params: generalParams,
                variables: data.variables,
                users: data.users,
                identifier: data.identifier || [],
                groupedItems: data.groupedItems || [],
            };
        }
    }, [data]);

    const filterScheduledTypeChange = (value: string) => {
        navigate(`/communication/template/${value}`);
    };

    const extractVariableName = (value: string): string | null => {
        const match = value?.match?.(/\{\{(.+?)\}\}/);
        return match ? match[1] : null;
    };

    const renderParamCard = (param: any, index: number, color: string, highlightVar: string | null) => {
        let hoverName: any = null;
        if (template.value.pk === 'MAIL_TEMPLATE')
            hoverName = param.block === 'CONTENT' ? param.field : extractVariableName(param.name);

        if (template.value.pk === 'WPP_TEMPLATE')
            hoverName = extractVariableName(param.block === 'CONTENT' ? param.field : param.name);

        const isHighlighted = hoverName !== null && hoverName === highlightVar;

        return (
            <div
                key={index}
                className={`col-span-6 bg-white border-l-4 rounded shadow px-4 py-3 border-${color}-500 mb-4 transition-all duration-200 ease-in-out hover:shadow-md ${isHighlighted ? 'ring-2 ring-amber-400 ring-offset-0' : ''}`}
                onMouseEnter={() => (hoveredVariable.value = hoverName)}
                onMouseLeave={() => (hoveredVariable.value = null)}
            >
                <div className="text-xs font-medium text-gray-500 mb-1 uppercase">{param.type}</div>
                <div className="flex items-center text-sm font-medium text-gray-500 space-x-2">
                    <span>{param.name}</span>
                    {param.field && (
                        <>
                            <IoArrowForwardOutline className="text-slate-700" />
                            <span className="text-slate-600">{param.field}</span>
                        </>
                    )}
                </div>
            </div>
        );
    };

    const SkeletonBox = ({ className = '' }: { className?: string }) => (
        <div className={`animate-pulse bg-gray-200 rounded ${className}`} />
    );

    const infoBody = (
        <div className="p-4 space-y-3 text-sm text-blue-800">
            <h4 className="text-base font-semibold text-blue-900">
                Formatos de campos permitidos para uso no modelo
            </h4>
            <div className="grid grid-cols-5 gap-4">
                <div className="col-span-3">
                    <h5 className="font-semibold">Formatos (tipos):</h5>
                    <ul className="list-disc pl-5 space-y-1">
                        <li>
                            <strong>date:</strong> campo de data no formato
                            <code> dd/mm/aaaa</code> (ex: <code>31/12/2024</code>).
                        </li>
                        <li>
                            <strong>currency:</strong> campo monetário formatado
                            com separadores e símbolo de moeda. (ex:
                            <code>R$ 9,05</code>).
                        </li>
                        <li>
                            <strong>number:</strong> apenas números inteiros ou
                            decimais (sem máscara).
                        </li>
                        <li>
                            <strong>text:</strong> texto livre, usado para
                            nomes, descrições, etc.
                        </li>
                        <li>
                            <strong>fileUrl:</strong> campo de URL de arquivos no
                            repositório (PDF, imagem, etc.).
                        </li>
                        <li>
                            <strong>govTaxId:</strong> campo de CNPJ com máscara
                            no formato <code>00.000.000/0001-00</code> e
                            validação automática.
                        </li>
                        <li>
                            <strong>socialId:</strong> campo de CPF com máscara
                            no formato <code>00.000.000/0001-00</code> e validação
                            automática.
                        </li>
                        <li>
                            <strong>phone:</strong> campo de número de telefone
                            com DDD no formato <code>(00)*0000-0000</code>.
                        </li>
                        <li>
                            <strong>email:</strong> campo de endereço de e-mail
                            válido.
                        </li>
                    </ul>
                </div>
                <div className="col-span-2">
                    <h5 className="font-semibold">Tamanho:</h5>
                    <p className="mt-1">
                        Limite máximo de caracteres permitidos para digitação.
                        Aplicável apenas aos campos:
                    </p>
                    <ul className="list-disc pl-5 mt-1 space-y-1">
                        <li>
                            <strong>number</strong>
                        </li>
                        <li>
                            <strong>text</strong>
                        </li>
                        <li>
                            <strong>email</strong>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    );

    return (
        <PageForm
            pageTitle={`${template.value?.description}`}
            apiVersion={apiVersion.value}
            isLoading={isLoading}
            breadcrumb={[{ label: 'Visualizar modelo', url: '#' }]}
            controlSlot={
                <div className="flex items-end justify-end space-x-4 mt-4">
                    <NewTooltip content="Boltar" alignment="left">
                        <button
                            type="button"
                            title="Boltar"
                            className="text-black bg-gray-300 hover:bg-gray-400 px-8 py-2.5 rounded"
                            onClick={() => navigate(-1)}
                        >
                            <IoMdArrowRoundBack />
                        </button>
                    </NewTooltip>
                </div>
            }
        >
            {isLoading ? (
                <div className="container mx-auto px-4 py-8 grid grid-cols-2 gap-4">
                    <div className="grid grid-cols-6 gap-4">
                        <SkeletonBox className="h-10 col-span-2" />
                        <SkeletonBox className="h-10 col-span-4" />

                        <div className="col-span-6 p-4 bg-white border border-gray-200 rounded-lg shadow-sm space-y-4">
                            <SkeletonBox className="h-4 w-1/4" />
                            <div className="grid grid-cols-12 gap-4">
                                {[...Array(2)].map((_, i) => (
                                    <SkeletonBox key={i} className="col-span-6 h-20" />
                                ))}
                            </div>
                        </div>

                        <div className="mt-4 p-4 bg-white border border-gray-200 rounded-lg shadow-sm space-y-4 col-span-6">
                            <SkeletonBox className="h-6 w-1/3" />
                            <SkeletonBox className="h-4 w-1/2" />
                            <div className="grid grid-cols-12 gap-4">
                                {[...Array(4)].map((_, i) => (
                                    <SkeletonBox key={i} className="col-span-6 h-24" />
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="col-span-1 p-4 bg-white border border-gray-200 rounded-lg shadow-sm">
                        <SkeletonBox className="h-4 w-1/3 mb-4" />
                        <div className="grid grid-cols-6 gap-2">
                            {[...Array(3)].map((_, i) => (
                                <SkeletonBox key={i} className="col-span-6 h-20" />
                            ))}
                        </div>
                    </div>
                </div>
            ) : (
                template.value && (
                    <div className="container mx-auto px-4 py-8">
                        {/* Abas para MAIL_TEMPLATE */}
                        {template.value.pk === 'MAIL_TEMPLATE' && (
                            <div className="mb-6">
                                <div className="border-b border-gray-200 dark:border-gray-700">
                                    <nav className="-mb-px flex space-x-8">
                                        <button
                                            onClick={() => activeTab.value = 'template'}
                                            className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab.value === 'template'
                                                ? 'border-blue-500 text-blue-600 dark:text-blue-400 dark:border-blue-400'
                                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                                                }`}
                                        >
                                            Template
                                        </button>
                                        <button
                                            onClick={() => activeTab.value = 'ses'}
                                            className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab.value === 'ses'
                                                ? 'border-blue-500 text-blue-600 dark:text-blue-400 dark:border-blue-400'
                                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                                                }`}
                                        >
                                            Templates SES ({sesTemplates.value.length})
                                        </button>
                                    </nav>
                                </div>
                            </div>
                        )}

                        <div className="grid grid-cols-2 gap-4">
                            {activeTab.value === 'template' ? (
                                <div className="grid grid-cols-6 gap-4">
                                    <TextEdit label="Tipo" value={template.value.pk} disabled className="col-span-2" readOnly />
                                    <TextEdit label="#:" value={template.value.sk} disabled className="col-span-4" readOnly />

                                    {template.value.identifier && template.value.identifier.length > 0 && (
                                        <div className="col-span-6">
                                            <div className="p-4 bg-white border border-gray-200 rounded-lg shadow-sm">
                                                <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wide mb-2">Agrupamento</h3>
                                                <SpanList
                                                    itemType={ErrorItemType.INFO}
                                                    items={template.value.identifier.map((item: string) => ({ label: item }))}
                                                />
                                            </div>
                                        </div>
                                    )}

                                    {template.value.groupedItems && template.value.groupedItems.length > 0 && (
                                        <div className="col-span-6">
                                            <div className="p-4 bg-white border border-gray-200 rounded-lg shadow-sm">
                                                <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wide mb-2">Campos agrupados</h3>
                                                <SpanList
                                                    itemType={ErrorItemType.INFO}
                                                    items={template.value.groupedItems.map((item: string) => ({ label: item }))}
                                                />
                                            </div>
                                        </div>
                                    )}

                                    <div className="col-span-6">
                                        <div className="p-4 bg-white border border-gray-200 rounded-lg shadow-sm">
                                            <div className="flex items-center mb-2">
                                                <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wide mr-1">
                                                    Destinatário
                                                </h3>
                                                <FaQuestionCircle
                                                    className="text-blue-500 cursor-pointer"
                                                    onClick={() => (showInfo.value = true)}
                                                />
                                            </div>
                                            <div className="grid grid-cols-12 gap-4">
                                                {template.value.params.map((param, i) =>
                                                    renderParamCard(param, i, 'blue', hoveredVariable.value)
                                                )}
                                            </div>
                                        </div>

                                        {template.value.templates && template.value.templates.length > 0 && (
                                            <div className="mt-4 p-4 bg-white border border-gray-200 rounded-lg shadow-sm">
                                                <div className="flex items-center justify-between mb-4">
                                                    <NewTooltip
                                                        content={
                                                            template.value.pk === 'MAIL_TEMPLATE'
                                                                ? 'Modelo no SES'
                                                                : 'Modelo na BLIP'
                                                        }
                                                    >
                                                        <h5 className="text-xl font-bold leading-none text-gray-900 dark:text-white">
                                                            <a
                                                                href={buildSesTemplateLink(template.value.templates[0].template_name, region, accountId)}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="text-blue-600 hover:underline"
                                                            >
                                                                {template.value.templates[0].template_name}
                                                            </a>
                                                        </h5>
                                                    </NewTooltip>

                                                    {template.value.pk === 'MAIL_TEMPLATE' && (
                                                        <NewTooltip
                                                            content={
                                                                <div className="text-sm w-48">
                                                                    Conjunto de configuração (Configuration Set)
                                                                </div>
                                                            }
                                                        >
                                                            <span className="text-sm font-medium text-gray-500">
                                                                <a
                                                                    href={buildSesConfigSetLink(template.value.templates[0].configuration_set || '', region, accountId)}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="text-blue-600 hover:underline"
                                                                >
                                                                    {template.value.templates[0].configuration_set}
                                                                </a>
                                                            </span>
                                                        </NewTooltip>
                                                    )}
                                                </div>

                                                <div className="col-span-12">
                                                    <div className="flex items-center mb-2">
                                                        <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wide mr-1">
                                                            {template.value.pk === 'MAIL_TEMPLATE'
                                                                ? 'Headers & Body (Email)'
                                                                : 'Headers & Content (WhatsApp)'}
                                                        </h3>
                                                        <FaQuestionCircle
                                                            className="text-blue-500 cursor-pointer"
                                                            onClick={() => (showInfo.value = true)}
                                                        />
                                                    </div>
                                                    <div className="grid grid-cols-12 gap-4">
                                                        {template.value.templates[0].params.map((param, i) =>
                                                            renderParamCard(
                                                                param,
                                                                i,
                                                                param.block === 'HEADER' ? 'red' : 'green',
                                                                hoveredVariable.value
                                                            )
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                /* Aba Templates SES */
                                <div className="col-span-2">
                                    <div className="space-y-4">
                                        {sesTemplates.value.length === 0 ? (
                                            <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                                                <p>Nenhum template SES encontrado</p>
                                            </div>
                                        ) : (
                                            sesTemplates.value.map((sesTemplate, index) => (
                                                <div key={index} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm">
                                                    <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                                                        <div className="flex items-center justify-between">
                                                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                                                {sesTemplate.name}
                                                            </h3>
                                                            <span className="text-xs text-gray-500 dark:text-gray-400">
                                                                {sesTemplate.createdTimestamp && new Date(sesTemplate.createdTimestamp).toLocaleDateString()}
                                                            </span>
                                                        </div>
                                                        {sesTemplate.subject && (
                                                            <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                                                                <strong>Assunto:</strong> {sesTemplate.subject}
                                                            </p>
                                                        )}
                                                    </div>
                                                    <div className="p-4">
                                                        {sesTemplate.textPart && (
                                                            <div className="mb-4">
                                                                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Texto:</h4>
                                                                <div className="bg-white border rounded p-4 whitespace-pre-wrap text-sm font-mono">
                                                                    {sesTemplate.textPart}
                                                                </div>
                                                            </div>
                                                        )}
                                                        {sesTemplate.htmlPart && (
                                                            <div>
                                                                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">HTML:</h4>
                                                                <div className="bg-white border rounded overflow-hidden">
                                                                    <iframe
                                                                        srcDoc={sesTemplate.htmlPart}
                                                                        className="w-full h-96 border-0"
                                                                        sandbox="allow-same-origin"
                                                                        title={`Preview do template ${sesTemplate.name}`}
                                                                    />
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>
                            )}

                            {activeTab.value === 'template' && (
                                <div>
                                    <div className="col-span-6 p-4 bg-white border border-gray-200 rounded-lg shadow-sm">
                                        <div className="flex items-center mb-2">
                                            <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wide mr-1">
                                                Variáveis
                                            </h3>
                                            <FaQuestionCircle
                                                className="text-blue-500 cursor-pointer"
                                                onClick={() => (showInfo.value = true)}
                                            />
                                        </div>
                                        <div className="grid grid-cols-6 gap-1">
                                            {template.value.variables.map((variable: any, index: number) => (
                                                <div
                                                    key={index}
                                                    className={`col-span-6 bg-white border-l-4 rounded shadow px-4 py-3 border-slate-500 mb-4 bg-stone-50 transition-all duration-200 ease-in-out hover:shadow-md ${hoveredVariable.value === variable.name
                                                        ? 'ring-2 ring-amber-400 ring-offset-0'
                                                        : ''
                                                        }`}
                                                    onMouseEnter={() => (hoveredVariable.value = variable.name)}
                                                    onMouseLeave={() => (hoveredVariable.value = null)}
                                                >
                                                    <div className="text-xs font-medium text-gray-500 mb-1">
                                                        {variable.display}
                                                        {variable.optional && <b className="ml-2 text-red-400">(Opcional)</b>}
                                                    </div>
                                                    <div className="flex items-center text-sm font-medium text-gray-500 w-full">
                                                        <div className="flex items-center space-x-2">
                                                            <span>{variable.name}</span>
                                                            <IoArrowForwardOutline className="text-slate-700" />
                                                            <span className="text-slate-600">{variable.field}</span>
                                                        </div>

                                                        <span
                                                            id={`optional-${index}`}
                                                            className="ml-auto text-slate-500 whitespace-nowrap text-xs font-normal text-slate-600"
                                                        >
                                                            {variable.format}
                                                            {variable.size > 0 && ` (${variable.size})`}
                                                        </span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="col-span-2 p-4 bg-white border border-gray-200 rounded-lg shadow-sm">
                                <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wide mb-2">Usuários</h3>
                                <SpanList
                                    itemType={ErrorItemType.SUCCESS}
                                    items={template.value.users.map((u: string) => ({ label: u }))}
                                />
                            </div>
                        </div>

                        <Dialog
                            id="info-dialog"
                            show={showInfo.value}
                            onClose={() => (showInfo.value = false)}
                            header="Informações sobre campos"
                            body={infoBody}
                        />
                    </div>
                )
            )}
        </PageForm>

    );
};

export default TemplateViewPage;
